import React, {
  createContext,
  useState,
  useEffect,
  useContext
} from 'react';
import { 
  onAuthStateChange, 
  sendSignInLink, 
  signOut as authSignOut,
  isValidSignInLink,
  confirmSignInWithEmailLink,
  getStoredEmailForSignIn
} from '../services/authService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    // Set up Firebase auth state listener
    const unsubscribe = onAuthStateChange((firebaseUser) => {
      setUser(firebaseUser);
      setLoggedIn(!!firebaseUser);
      setLoading(false);
    });

    // Handle magic link sign-in on page load
    const handleMagicLinkSignIn = async () => {
      if (isValidSignInLink()) {
        setLoading(true);
        
        // Try to extract email from URL parameters (embedded in the magic link)
        const urlParams = new URLSearchParams(window.location.search);
        let email = urlParams.get('email');
        
        // Fallback to localStorage if email not in URL
        if (!email) {
          email = getStoredEmailForSignIn();
        }
        
        // If we have an email, complete the sign-in
        if (email) {
          const result = await confirmSignInWithEmailLink(email, window.location.href);
          if (result.success) {
            // Clear the URL to remove sign-in parameters
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            console.error('Magic link sign-in failed:', result.error);
            setLoading(false);
          }
        } else {
          console.error('Could not determine email for sign-in');
          setLoading(false);
        }
      }
    };

    handleMagicLinkSignIn();

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  const sendMagicLink = async (email) => {
    return await sendSignInLink(email);
  };

  const signOut = async () => {
    const result = await authSignOut();
    if (result.success) {
      setUser(null);
      setLoggedIn(false);
    }
    return result;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loggedIn,
        loading,
        sendMagicLink,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
